import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, LogOut, LayoutDashboard, ShieldCheck, Sparkles, Facebook, Instagram, Youtube, ShoppingBag, MapPin, BadgeCheck, Lock, Users, Mail, Globe, UserRound, ChevronDown } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "../ui/dropdown-menu";
import { Logo, HeaderBrand } from "../common/ui";
import AgeBadge from "../common/AgeBadge";
import { Button } from "../ui/button";
import SeoHead from "../seo/SeoHead";
import { useAuth, STAFF_ROLES } from "../../context/AuthContext";
import { useShop } from "../../context/ShopContext";
import api from "../../lib/api";

const PUBLIC_LINKS = [
  { to: "/", label: "Home", end: true },
  { to: "/fireworks", label: "Fireworks" },
  { to: "/offers", label: "Offers" },
  { to: "/click-and-collect", label: "Click & Collect" },
  { to: "/safety", label: "Safety" },
  { to: "/contact", label: "Contact" },
];

function AnnouncementBar() {
  const [text, setText] = useState("");
  useEffect(() => {
    api.get("/content/homepage").then(({ data }) => {
      if (data?.announcement_bar?.enabled) setText(data.announcement_bar.text || "");
    }).catch(() => {});
  }, []);
  const message = text || "Join our mailing list and get 10% off your first order";
  return (
    <div data-testid="announcement-bar" className="w-full bg-gradient-to-r from-firered/20 via-fireorange/15 to-amber/20 border-b border-white/10">
      <Link to="/vip" data-testid="announcement-vip-link" className="group flex items-center justify-center gap-2 text-center text-[11px] sm:text-xs tracking-[0.15em] uppercase font-semibold text-white hover:text-white py-2 px-4 transition-colors">
        <Sparkles className="h-3.5 w-3.5 text-gold" />
        <span>{message}</span>
        <span className="text-gold underline underline-offset-2">Join our VIP list</span>
      </Link>
    </div>
  );
}

export function SiteHeader() {
  const { user, logout } = useAuth();
  const { basket } = useShop();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [storesTo, setStoresTo] = useState("/stores");
  const navigate = useNavigate();
  const location = useLocation();
  const isStaff = user && STAFF_ROLES.includes(user.role);
  const overHero = location.pathname === "/";

  useEffect(() => {
    api.get("/stores").then(({ data }) => {
      if (Array.isArray(data) && data.length === 1 && data[0]?.slug) setStoresTo(`/stores/${data[0].slug}`);
      else setStoresTo("/stores");
    }).catch(() => {});
  }, []);

  const links = [
    ...PUBLIC_LINKS.slice(0, 5),
    { to: storesTo, label: "Our Stores" },
    ...PUBLIC_LINKS.slice(5),
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = scrolled || !overHero || open;

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        solid ? "cf-glass border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
      data-testid="public-header"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 h-[100px] md:h-[140px] flex items-center justify-between gap-4">
        <HeaderBrand />
        <nav className="hidden xl:flex items-center gap-0 xl:ml-10" aria-label="Primary">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `px-2.5 py-2 text-sm rounded-md transition-colors whitespace-nowrap font-medium outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-ink ${isActive ? "text-gold" : "text-white/90 hover:text-white"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-2">
          {!user ? (
            <>
              <Button variant="ghost" data-testid="header-login-btn" className="text-white/90 hover:text-white hover:bg-white/5" onClick={() => navigate("/login")}>Sign In / Register</Button>
              <Button data-testid="header-vip-btn" className="cf-gold-btn rounded-full font-semibold gap-2" onClick={() => navigate("/vip")}>
                <Sparkles className="h-4 w-4" /> VIP List
              </Button>
            </>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" data-testid="header-account-menu" className="text-white/90 hover:text-white hover:bg-white/5 gap-2">
                  <UserRound className="h-4 w-4" /> My Account <ChevronDown className="h-3.5 w-3.5 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-ink border-white/10 text-white/90 min-w-[200px]">
                {isStaff ? (
                  <DropdownMenuItem data-testid="menu-admin" className="gap-2 cursor-pointer focus:bg-white/10 focus:text-white" onClick={() => navigate("/admin")}>
                    <ShieldCheck className="h-4 w-4" /> Admin
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem data-testid="menu-reservations" className="gap-2 cursor-pointer focus:bg-white/10 focus:text-white" onClick={() => navigate("/dashboard/reservations")}>
                    <ShoppingBag className="h-4 w-4" /> My Reservations
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid="menu-account" className="gap-2 cursor-pointer focus:bg-white/10 focus:text-white" onClick={() => navigate("/dashboard")}>
                  <UserRound className="h-4 w-4" /> My Account
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem data-testid="menu-logout" className="gap-2 cursor-pointer text-gold focus:bg-gold/10 focus:text-gold" onClick={() => logout().then(() => navigate("/"))}>
                  <LogOut className="h-4 w-4" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <div className="flex items-center gap-1">
          <Link to="/basket" data-testid="header-basket-btn" className="relative p-2 text-white/80 hover:text-gold transition-colors" aria-label="Basket">
            <ShoppingBag className="h-5 w-5" />
            {basket.count > 0 && <span data-testid="basket-count" className="absolute top-0 right-0 bg-gold text-ink text-[10px] font-bold rounded-full h-4 min-w-[16px] px-1 grid place-items-center">{basket.count}</span>}
          </Link>
          <button className="xl:hidden text-white p-2" data-testid="mobile-menu-toggle" onClick={() => setOpen((v) => !v)} aria-label="Toggle menu" aria-expanded={open}>
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden border-t border-white/10 px-4 py-4 space-y-1 cf-glass" data-testid="mobile-menu">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setOpen(false)}
              data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) => `block px-3 py-2.5 rounded-md text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-gold ${isActive ? "text-gold bg-white/5" : "text-white/90"}`}>
              {l.label}
            </NavLink>
          ))}
          <div className="pt-3 flex flex-col gap-2">
            {!user ? (
              <>
                <Button variant="outline" className="w-full border-white/20 text-white" onClick={() => { setOpen(false); navigate("/login"); }}>Sign In / Register</Button>
                <Button className="w-full cf-gold-btn font-semibold gap-2" onClick={() => { setOpen(false); navigate("/vip"); }}><Sparkles className="h-4 w-4" /> VIP List</Button>
              </>
            ) : (
              <>
                {isStaff && <Button variant="outline" className="w-full border-white/20 text-white" onClick={() => { setOpen(false); navigate("/admin"); }}>Admin Portal</Button>}
                <Button variant="outline" data-testid="mobile-account-btn" className="w-full border-white/20 text-white" onClick={() => { setOpen(false); navigate("/dashboard"); }}>My Account</Button>
                <Button variant="outline" data-testid="mobile-reservations-btn" className="w-full border-white/20 text-white" onClick={() => { setOpen(false); navigate("/dashboard/reservations"); }}>My Reservations</Button>
                <Button className="w-full cf-gold-btn font-semibold" onClick={() => { setOpen(false); logout().then(() => navigate("/")); }}>Sign Out</Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

export default function PublicLayout() {
  const [storesTo, setStoresTo] = useState("/stores");
  useEffect(() => {
    api.get("/stores").then(({ data }) => {
      if (Array.isArray(data) && data.length === 1 && data[0]?.slug) setStoresTo(`/stores/${data[0].slug}`);
      else setStoresTo("/stores");
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen cf-bg cf-grain relative" style={{ overflowX: "clip" }}>
      <SeoHead />
      <div className="relative z-10">
        <AnnouncementBar />
        <SiteHeader />
        <main>
          <Outlet />
        </main>
        <Footer storesTo={storesTo} />
      </div>
    </div>
  );
}

export function Footer({ storesTo = "/stores" }) {
  const cols = [
    { title: "Shop", links: [["Fireworks", "/fireworks"], ["Brands", "/brands"], ["Offers", "/offers"], ["Click & Collect", "/click-and-collect"], ["Our Stores", storesTo], ["Areas We Serve", "/areas-we-serve"], ["VIP List", "/vip"]] },
    { title: "Customer Service", links: [["Contact Us", "/contact"], ["Returns", "/returns"], ["Safety Advice", "/safety"]] },
    { title: "Legal", links: [["Privacy Policy", "/privacy"], ["Terms & Conditions", "/terms"], ["Cookie Policy", "/cookies"], ["Age Policy", "/safety"]] },
  ];
  const trust = [
    { icon: ShoppingBag, label: "Reserve Online" },
    { icon: MapPin, label: "Click & Collect" },
    { icon: BadgeCheck, label: "CE Certified Fireworks" },
    { icon: Lock, label: "Secure Checkout" },
    { icon: Users, label: "Family Business" },
  ];
  return (
    <footer className="border-t border-white/10 mt-24" data-testid="site-footer">
      {/* Trust strip */}
      <div className="border-b border-white/10 bg-white/[0.02]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-5 flex flex-wrap items-center justify-center gap-x-8 gap-y-3" data-testid="footer-trust-section">
          {trust.map(({ icon: Icon, label }) => (
            <div key={label} data-testid={`trust-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="flex items-center gap-2 text-xs sm:text-sm text-white/70">
              <Icon className="h-4 w-4 text-gold shrink-0" />
              <span className="whitespace-nowrap">{label}</span>
            </div>
          ))}
          <AgeBadge size={32} linked className="text-xs sm:text-sm" />
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 grid gap-10 md:grid-cols-5">
        <div className="md:col-span-2">
          <Logo />
          <p className="mt-4 text-sm text-muted-foreground max-w-sm">
            Premium fireworks in Clacton-on-Sea, Essex. Reserve online, pay and collect in store. A Challenge 25 responsible retailer.
          </p>
          <address className="mt-5 not-italic text-sm text-muted-foreground leading-relaxed" data-testid="footer-address">
            <span className="block font-semibold text-white/85">Clacton Fireworks</span>
            Weeley Post Office &amp; Store<br />
            The Street, Weeley<br />
            Essex, CO16 9JA
          </address>
          <div className="mt-4 space-y-1 text-sm">
            <a href="mailto:info@clactonfireworks.co.uk" data-testid="footer-email" className="flex items-center gap-2 text-muted-foreground hover:text-gold transition-colors">
              <Mail className="h-4 w-4 text-gold/80" /> info@clactonfireworks.co.uk
            </a>
            <a href="https://clactonfireworks.co.uk" target="_blank" rel="noopener noreferrer" data-testid="footer-website" className="flex items-center gap-2 text-muted-foreground hover:text-gold transition-colors">
              <Globe className="h-4 w-4 text-gold/80" /> clactonfireworks.co.uk
            </a>
          </div>
          <div className="mt-5 flex items-center gap-3">
            {[Facebook, Instagram, Youtube].map((Icon, i) => (
              <a key={i} href="#" aria-label="social" className="grid place-items-center h-9 w-9 rounded-full border border-white/15 text-white/70 hover:text-gold hover:border-gold/40 transition-colors" data-testid={`social-${i}`}>
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>
        {cols.map((c) => (
          <div key={c.title}>
            <h4 className="font-cinzel text-xs tracking-[0.2em] uppercase text-gold mb-4">{c.title}</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              {c.links.map(([label, to]) => (
                <li key={label}><Link to={to} data-testid={`footer-link-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="hover:text-white transition-colors">{label}</Link></li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-white/10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} Clacton Fireworks. All rights reserved.</p>
          <p className="text-xs font-semibold text-gold flex items-center gap-2" data-testid="age-notice">
            <ShieldCheck className="h-4 w-4" /> You must be 18+ to purchase fireworks. Challenge 25 applies.
          </p>
        </div>
      </div>
    </footer>
  );
}
